import { queryOptions } from "@tanstack/react-query";
import { authenticatedRequest } from "@/lib/graphql/client";
import {
  ADMIN_VIEWER_FILE_CONTENT,
  ADMIN_VIEWER_USER_WORKSPACES,
  ADMIN_VIEWER_WORKSPACE_TREE,
} from "@/lib/graphql/queries/user-data-viewer";
import type {
  ViewerFileContent,
  ViewerTreeNode,
  ViewerUserWorkspacesResult,
} from "@/types/user-data-viewer";

export const userDataViewerKeys = {
  all: ["userDataViewer"] as const,
  workspaces: (emailOrId: string) =>
    [...userDataViewerKeys.all, "workspaces", emailOrId] as const,
  tree: (workspaceId: string) =>
    [...userDataViewerKeys.all, "tree", workspaceId] as const,
  file: (fileId: string) =>
    [...userDataViewerKeys.all, "file", fileId] as const,
};

export function userWorkspacesQueryOptions(emailOrId: string | null) {
  return queryOptions({
    queryKey: userDataViewerKeys.workspaces(emailOrId ?? ""),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminViewerUserWorkspaces: ViewerUserWorkspacesResult;
      }>(ADMIN_VIEWER_USER_WORKSPACES, { emailOrId });
      return data.adminViewerUserWorkspaces;
    },
    enabled: Boolean(emailOrId),
    staleTime: 30000,
  });
}

export function workspaceTreeQueryOptions(workspaceId: string | null) {
  return queryOptions({
    queryKey: userDataViewerKeys.tree(workspaceId ?? ""),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminViewerWorkspaceTree: ViewerTreeNode[];
      }>(ADMIN_VIEWER_WORKSPACE_TREE, { workspaceId });
      return data.adminViewerWorkspaceTree;
    },
    enabled: Boolean(workspaceId),
    staleTime: 30000,
  });
}

export function fileContentQueryOptions(fileId: string | null) {
  return queryOptions({
    queryKey: userDataViewerKeys.file(fileId ?? ""),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminViewerFileContent: ViewerFileContent;
      }>(ADMIN_VIEWER_FILE_CONTENT, { fileId });
      return data.adminViewerFileContent;
    },
    enabled: Boolean(fileId),
    staleTime: 60000,
  });
}
